import React, { useCallback, useState, useEffect } from "react";
import get from "lodash/get";
import fetch from "mibaby-common/util/fetch";
import {
  LikeButton as LikeBtn,
  CommentButton as CommentBtn,
  ShareButton,
  SocialButtons as SocialButtonsBase,
} from "mibaby-frontend/social/Buttons";
import { useUser } from "@store/profile";
import { useAnonymousInterceptor } from "@components/with-anonymous";
import { CommentContainer, useCommentCount, useCommentsLoaded, getArgs } from "@components/comments";
import useWindow from "mibaby-frontend/util/hooks/useWindow";
import useUrl from "mibaby-frontend/util/hooks/useUrl";
import { useInView } from "react-intersection-observer";

export const getLikeCount = teaser =>
  get(teaser, "AGG_LIKE.sum", 0) +
  ((teaser.social_buttons &&
    get(
      teaser.social_buttons.find(o => ["LIKE", "UPDOWNVOTE"].indexOf(o.type) > -1 && o.fakeCount),
      "fakeCount"
    )) ||
    0);

export const LikeButton = ({
  id,
  ups: initialCount = 0,
  userLike: initialUserLike = false,
  fakeCount = 0,
  userOnly = false,
  registerLightboxId,
  onClick,
  ...props
}) => {
  const { loggedIn } = useUser();

  const [{ count, userLike }, setState] = useState({
    count: initialCount + fakeCount,
    userLike: initialUserLike,
  });

  const pvid = useWindow("miBaby.pvid", 0);

  const setLiked = useCallback(
    (value = true) => {
      setState(state => {
        if (value !== state.userLike) {
          return {
            count: state.count + (value ? 1 : -1),
            userLike: value,
          };
        }
      });
      const [, curId] = id.split("_");
      fetch(`/content_feeds/like/${curId}?pvid=${pvid}`, {
        method: value === true ? "POST" : "DELETE",
      })
        .then(r => r.json())
        .then(v => {
          if (v === "error_model_null") {
            alert("Dieser Teaser hat keinen Content.");
          }
        });
      onClick && onClick(curId, value);
    },
    [id, pvid]
  );
  const [userOnlySetLiked] = useAnonymousInterceptor(setLiked, null, registerLightboxId);

  return (
    <LikeBtn
      count={count}
      isLiked={userLike}
      shouldReward={!userOnly || loggedIn}
      handleLikeToggle={() => (userOnly ? userOnlySetLiked(!userLike) : setLiked(!userLike))}
      {...props}
    />
  );
};

export const CommentButton = React.forwardRef(({ modelType, modelId, onClick }, ref) => {
  const count = useCommentCount(getArgs(modelType, modelId)) || 0;
  const handleClick = () => onClick && onClick(modelType, modelId);

  return <CommentBtn ref={ref} count={count} onClick={handleClick} />;
});

/**
 * @param {{
 *  id: string,
 *  buttons: {type: "LIKE", reward: string, fakeCount: number}[],
 *  AGG_LIKE: {
 *    count: number,
 *    userLike: -1 | 0 | 1
 *    value: number,
 *    ups: number,
 *    downs: number
 *  }
 * }} param0
 */
export const SocialButtons = ({
  id,
  teaserId,
  buttons = [],
  AGG_LIKE,
  showCommentBox = true,
  commentProps = {},
  ...props
}) => {
  const url = useUrl(true, { ank: teaserId });

  // Comments handling
  const comment = buttons.find(p => p.type === "COMMENT");
  const [commentButtonRef, autoloadComments] = useInView();
  const [showComments, setShowComments] = useState(comment && showCommentBox);
  const [loadComments, setLoadComments] = useState(false);
  const loaded = useCommentsLoaded(getArgs("ContentTeaser", teaserId));
  const handleCommentsClick = () =>
    commentProps.onLoad
      ? commentProps.onLoad(() => setLoadComments(true))
      : loaded
      ? setShowComments(v => !v)
      : setLoadComments(true);

  return (
    <>
      <SocialButtonsBase {...props}>
        {buttons.map(({ type, ...props }) => {
          switch (type) {
            case "LIKE":
              return <LikeButton {...props} {...AGG_LIKE} type={props.reward} id={id} key={type} />;
            case "COMMENT":
              return (
                <CommentButton
                  {...props}
                  ref={commentButtonRef}
                  modelType="ContentTeaser"
                  modelId={teaserId}
                  onClick={handleCommentsClick}
                  key={type}
                />
              );
            case "SHARE":
              return <ShareButton {...props} teaserId={teaserId} key={type} />;
          }
        })}
      </SocialButtonsBase>
      {showComments && (
        <CommentContainer
          key="comments"
          modelType="ContentTeaser"
          modelId={teaserId}
          autoload={loadComments || (autoloadComments && { top: true })}
          lightboxId={comment.registerLightboxId || undefined}
          lightboxArgs={{ activation_redirect: url }}
          {...commentProps}
        />
      )}
    </>
  );
};

export default SocialButtons;
